import dayjs from 'dayjs';
import { GuidT, NewUserStorageDataIF } from '@/types/common';
import { getLocalStorageItem, setLocalStorageItem } from '@/helpers/storage';
import ImgNoImage from '@/assets/images/noimage.webp';
import { STORAGE_KEYS } from '@/constants/storageKeys';

const NUM_5 = 5;
const NUM_05 = 0.5;
export const DEBOUNCE = 700;

export const isTelegramApp = (): boolean => !!window.Telegram?.WebApp?.initData;

export const getFormattedPathName = (pathname: string): string =>
    pathname.length > 1 && pathname.endsWith('/')
        ? pathname.slice(0, -1)
        : pathname;

export const base64ToFile = (
    base64: string,
    filename: string = `webcam-${Date.now()}.jpg`,
): File => {
    const array = base64.split(',');
    const mime = array[0].match(/:(.*?);/)![1];
    const bstr = atob(array[1]);
    let n = bstr.length;
    const u8array = new Uint8Array(n);

    while (n--) {
        u8array[n] = Number(bstr.codePointAt(n));
    }

    return new File([u8array], filename, { type: mime });
};

export const img = (imgId?: GuidT | null): string =>
    imgId ? `${import.meta.env.VITE_API_ENDPOINT}/Files/${imgId}` : ImgNoImage;

export const formatDate = (date?: string | null): string => {
    if (!date || !dayjs(date).isValid()) {
        return '';
    }

    return dayjs(date).format('DD.MM.YYYY');
};

export const normalizeDate = (date: string): string =>
    dayjs(date).endOf('day').toISOString();

export const getRandomValuesFromObject = <T = string>(
    data?: Record<string, T[]> | null,
    sliceCount = 7,
): T[] | null => {
    if (!data || Object.keys(data).length === 0) {
        return null;
    }

    const allItems = Object.values(data).flat().filter(Boolean);

    if (allItems.length === 0) {
        return null;
    }

    const actualSliceCount = Math.min(sliceCount, allItems.length);

    return allItems
        .toSorted(() => Math.random() - NUM_05)
        .slice(0, actualSliceCount);
};

export const debounce = <Arguments extends unknown[], ReturnType>(
    asyncFunction: (...argumentsList: Arguments) => ReturnType,
    delay = DEBOUNCE,
) => {
    let timerId: number;
    let lastReject: ((reason?: unknown) => void) | null = null;

    return (...argumentsList: Arguments): Promise<Awaited<ReturnType>> =>
        new Promise<Awaited<ReturnType>>((resolve, reject) => {
            if (lastReject) {
                lastReject('cancelled');
            }

            lastReject = reject;

            clearTimeout(timerId);
            timerId = window.setTimeout(async () => {
                try {
                    const result = await asyncFunction(...argumentsList);
                    resolve(result as Awaited<ReturnType>);
                } catch (error) {
                    reject(error);
                }
            }, delay);
        });
};

export const formatTimeWithoutSeconds = (value?: string): string | null =>
    value ? value.slice(0, NUM_5) : '';

export const delay = (ms: number): Promise<(ms: number) => void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

export const syncNewUserStorageData = (
    newItem: Partial<NewUserStorageDataIF>,
): NewUserStorageDataIF | null => {
    const data = getLocalStorageItem<NewUserStorageDataIF>(
        STORAGE_KEYS.NewUserFlags,
    );

    if (!data) {
        return null;
    }

    const newData = {
        ...data,
        ...newItem,
    };

    const result = Object.values(newData).some(Boolean) ? newData : null;

    setLocalStorageItem<NewUserStorageDataIF>(
        STORAGE_KEYS.NewUserFlags,
        result,
    );

    return result;
};
